import React, { useEffect, useState } from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  message
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  CopyOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import JoinTeamModal from '../components/JoinTeamModal';
import CreateTeamModal from '../components/CreateTeamModal';
import {
  fetchCurrentTeam,
  patchCurrentTeam,
  TenantTeamDetail
} from '../services/api';
import type { TeamCategoryKey } from '../components/CreateTeamModal';
import dayjs from 'dayjs';
import { getDemoTeamProfileRows } from '../demo/demoBrowseMocks';
import './TeamManagement.css';

const { TextArea } = Input;

const DEMO_TEAM_WELCOME_SESSION_KEY = 'lims_demo_team_welcome_v1';

const CATEGORY_OPTIONS: { value: TeamCategoryKey; label: string }[] = [
  { value: 'enterprise', label: '企业/教培机构' },
  { value: 'school', label: '学校/科研院所' },
  { value: 'research_group', label: '高校课题组' },
  { value: 'hospital', label: '医院/医学院' },
  { value: 'government', label: '政府/事业单位' }
];

const TEAM_SIZE_OPTIONS = ['1-10人', '11-50人', '51-200人', '200人以上'].map((v) => ({
  value: v,
  label: v
}));
const POSITION_OPTIONS = [
  '负责人',
  'PI/导师',
  '实验室主任',
  '科研人员',
  '行政/助理',
  '学生',
  '其他'
].map((v) => ({ value: v, label: v }));
const INDUSTRY_OPTIONS = [
  '高等院校',
  '科研院所',
  '医疗卫生',
  '生物医药',
  '新材料/化工',
  '电子信息',
  '政府/事业单位',
  '企业',
  '其他'
].map((v) => ({ value: v, label: v }));
const RESEARCH_OPTIONS = [
  '生命科学',
  '化学/材料',
  '医学/药学',
  '物理',
  '计算机/人工智能',
  '环境',
  '综合',
  '其他'
].map((v) => ({ value: v, label: v }));

function paidSubscriptionActive(team: TenantTeamDetail): boolean {
  if (team.planTier !== 'paid' || !team.subscriptionEndsAt) return false;
  return new Date(team.subscriptionEndsAt).getTime() > Date.now();
}

function labelFromOptions(
  value: string | null | undefined,
  options: { value: string; label: string }[]
): string {
  if (!value) return '—';
  return options.find((o) => o.value === value)?.label ?? value;
}

const TeamManagement: React.FC = () => {
  const navigate = useNavigate();
  const { role, isDemoBrowseOnly, teamId } = useAuth();
  const [joinOpen, setJoinOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<TenantTeamDetail | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [freeMax, setFreeMax] = useState(10);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const canEdit = role === '管理员' || role === '超级管理员';
  const showGate = isDemoBrowseOnly || !teamId;

  const load = async () => {
    if (showGate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchCurrentTeam();
      setTeam(data.team);
      setMemberCount(data.memberCount);
      setFreeMax(data.freeTierMaxMembers);
      form.setFieldsValue({
        name: data.team.name,
        category: data.team.category || undefined,
        orgName: data.team.orgName || '',
        teamSize: data.team.teamSize || undefined,
        ownerPosition: data.team.ownerPosition || undefined,
        industry: data.team.industry || undefined,
        researchField: data.team.researchField || undefined,
        intro: data.team.intro || '',
        contactName: data.team.contactName || '',
        contactPhone: data.team.contactPhone || '',
        contactEmail: data.team.contactEmail || ''
      });
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGate]);

  useEffect(() => {
    if (!showGate || !isDemoBrowseOnly) {
      setWelcomeOpen(false);
      return;
    }
    try {
      if (sessionStorage.getItem(DEMO_TEAM_WELCOME_SESSION_KEY) === '1') return;
    } catch {
      /* private mode */
    }
    setWelcomeOpen(true);
  }, [showGate, isDemoBrowseOnly]);

  const markDemoWelcomeSeen = () => {
    try {
      sessionStorage.setItem(DEMO_TEAM_WELCOME_SESSION_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const dismissDemoWelcome = () => {
    markDemoWelcomeSeen();
    setWelcomeOpen(false);
  };

  const openJoinFromWelcome = () => {
    markDemoWelcomeSeen();
    setWelcomeOpen(false);
    setJoinOpen(true);
  };

  const openCreateFromWelcome = () => {
    markDemoWelcomeSeen();
    setWelcomeOpen(false);
    setCreateOpen(true);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!canEdit || !team) return;
    setSaving(true);
    try {
      const res = await patchCurrentTeam({
        name: String(values.name || '').trim(),
        category: (values.category as string | undefined) ?? null,
        orgName: String(values.orgName || '').trim(),
        teamSize: values.teamSize as string | undefined,
        ownerPosition: values.ownerPosition as string | undefined,
        industry: values.industry as string | undefined,
        researchField: values.researchField as string | undefined,
        intro: String(values.intro || '').trim(),
        contactName: String(values.contactName || '').trim(),
        contactPhone: String(values.contactPhone || '').trim(),
        contactEmail: String(values.contactEmail || '').trim()
      });
      if (res.success && res.team) {
        message.success('已保存');
        setTeam(res.team);
      } else {
        message.error(res.error || '保存失败');
      }
    } finally {
      setSaving(false);
    }
  };

  const copyTeamCode = (code: string) => {
    void navigator.clipboard.writeText(code).then(
      () => message.success('已复制团队编码'),
      () => message.error('复制失败，请手动复制')
    );
  };

  if (showGate) {
    return (
      <div className="page-container full-height team-mgmt-stitch tm-gate">
        <Modal
          title="演示团队浏览模式"
          open={welcomeOpen && isDemoBrowseOnly}
          onCancel={dismissDemoWelcome}
          footer={null}
          destroyOnHidden
          width={520}
          centered
        >
          <div className="lims-modal-callout tm-welcome-callout" role="presentation">
            <InfoCircleOutlined aria-hidden />
            <span>
              您当前尚未加入真实实验室团队，系统以<strong>演示团队</strong>展示示例数据，写入类操作不可用。
            </span>
          </div>
          <p className="tm-welcome-lead">
            向管理员索取<strong>团队编码</strong>即可加入；若您需要自建实验室空间，可选择创建团队并完成资料填写。
          </p>
          <div className="tm-welcome-actions">
            <button type="button" className="tm-gate-btn tm-gate-btn--secondary" onClick={openJoinFromWelcome}>
              <UserOutlined aria-hidden />
              加入团队
            </button>
            <button type="button" className="tm-gate-btn tm-gate-btn--primary" onClick={openCreateFromWelcome}>
              <TeamOutlined aria-hidden />
              创建团队
            </button>
            <button type="button" className="tm-gate-link" onClick={dismissDemoWelcome}>
              稍后再说
            </button>
          </div>
        </Modal>

        <JoinTeamModal open={joinOpen} onClose={() => setJoinOpen(false)} />
        <CreateTeamModal open={createOpen} onClose={() => setCreateOpen(false)} />

        <Breadcrumb
          className="tm-breadcrumb"
          items={[
            { title: <Link to="/dashboard">首页</Link> },
            { title: isDemoBrowseOnly ? '演示团队 / 团队管理' : '团队管理' }
          ]}
        />

        <header className="tm-hero tm-gate-hero">
          <div className="tm-hero-icon" aria-hidden>
            <TeamOutlined />
          </div>
          <div>
            <h1 className="tm-title">{isDemoBrowseOnly ? '演示团队' : '团队管理'}</h1>
            <p className="tm-lead">
              {isDemoBrowseOnly
                ? '可先浏览界面与示例内容；加入或创建团队后即可管理本实验室资料与成员。'
                : '加入或创建团队后，可在此管理团队资料与成员。'}
            </p>
          </div>
        </header>

        <Alert
          type="info"
          showIcon
          message={isDemoBrowseOnly ? '当前为演示浏览模式' : '当前未加入团队'}
          description="使用下方或顶栏的「加入团队」「创建团队」进入真实实验室空间后，即可使用完整团队管理能力。"
        />

        {isDemoBrowseOnly ? (
          <>
            <div className="tm-gate-demo-profile" style={{ marginTop: 24, maxWidth: 720 }}>
              <h2 style={{ fontSize: '1.1rem', marginBottom: 12, fontWeight: 600 }}>示例团队资料</h2>
              <dl style={{ margin: 0 }}>
                {getDemoTeamProfileRows().map((row) => (
                  <div
                    key={row.k}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1fr',
                      gap: '8px 16px',
                      padding: '10px 0',
                      borderBottom: '1px solid rgba(0,0,0,0.06)'
                    }}
                  >
                    <dt style={{ margin: 0, color: 'rgba(0,0,0,0.45)' }}>{row.k}</dt>
                    <dd style={{ margin: 0 }}>{row.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="tm-gate-actions">
              <button type="button" className="tm-gate-btn tm-gate-btn--secondary" onClick={() => setJoinOpen(true)}>
                <UserOutlined aria-hidden />
                加入团队
              </button>
              <button type="button" className="tm-gate-btn tm-gate-btn--primary" onClick={() => setCreateOpen(true)}>
                <TeamOutlined aria-hidden />
                创建团队
              </button>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  if (loading || !team) {
    return (
      <div className="page-container full-height team-mgmt-stitch tm-loading">
        <Spin size="large" />
      </div>
    );
  }

  const suspended = team.status === 'suspended';
  const paidOk = paidSubscriptionActive(team);
  const atFreeCeiling = !paidOk && memberCount >= freeMax;
  const isFreeTier = team.planTier !== 'paid';

  const billingTitle = team.planLabel || (team.planTier === 'paid' ? '付费版' : '免费版');

  const memberDisplay = paidOk ? `${memberCount} 人` : `${memberCount} / ${freeMax} 人`;

  const planNote = [team.planLabel, isFreeTier ? '免费版人数受平台限制；升级付费可解除人数上限并获得更多能力。' : null]
    .filter(Boolean)
    .join(' ');

  const handleDiscard = () => {
    form.setFieldsValue({
      name: team.name,
      category: team.category || undefined,
      orgName: team.orgName || '',
      teamSize: team.teamSize || undefined,
      ownerPosition: team.ownerPosition || undefined,
      industry: team.industry || undefined,
      researchField: team.researchField || undefined,
      intro: team.intro || '',
      contactName: team.contactName || '',
      contactPhone: team.contactPhone || '',
      contactEmail: team.contactEmail || ''
    });
  };

  const readOnlyRows: { k: string; v: string }[] = [
    { k: '团队名称', v: team.name },
    { k: '团队类型', v: labelFromOptions(team.category ?? undefined, CATEGORY_OPTIONS) },
    { k: '单位名称', v: team.orgName || '—' },
    { k: '团队规模', v: labelFromOptions(team.teamSize ?? undefined, TEAM_SIZE_OPTIONS) },
    { k: '您的职务', v: labelFromOptions(team.ownerPosition ?? undefined, POSITION_OPTIONS) },
    { k: '所在行业', v: labelFromOptions(team.industry ?? undefined, INDUSTRY_OPTIONS) },
    { k: '研究领域', v: labelFromOptions(team.researchField ?? undefined, RESEARCH_OPTIONS) },
    { k: '简介', v: team.intro || '—' },
    { k: '联系人', v: team.contactName || '—' },
    { k: '联系电话', v: team.contactPhone || '—' },
    { k: '联系邮箱', v: team.contactEmail || '—' }
  ];

  return (
    <div className="page-container full-height team-mgmt-stitch">
      <Breadcrumb
        className="tm-breadcrumb"
        items={[
          { title: <Link to="/dashboard">首页</Link> },
          { title: '团队管理' }
        ]}
      />

      <header className="tm-hero">
        <div className="tm-hero-icon" aria-hidden>
          <TeamOutlined />
        </div>
        <div>
          <h1 className="tm-title">团队管理</h1>
          <p className="tm-lead">维护本团队公开资料；成员账号请在「成员管理」中操作。</p>
        </div>
      </header>

      {suspended && (
        <div className="tm-strip tm-strip--error" role="alert">
          <WarningOutlined className="tm-strip-icon" />
          <p>
            团队已停用。常见于：付费订阅已到期且当前人数超过免费版上限。请联系平台续费，或将人数精简到免费额度内后请平台管理员恢复团队状态。
          </p>
        </div>
      )}

      {!suspended && atFreeCeiling && (
        <div className="tm-strip tm-strip--limit" role="status">
          <WarningOutlined className="tm-strip-icon" />
          <p>
            免费版人数已达上限（{freeMax}
            人）。添加新成员前需升级订阅（由平台在后台开通付费并设置到期日），或先移除成员。
          </p>
          <Button
            type="text"
            className="tm-strip-action"
            onClick={() => message.info('请联系平台管理员开通付费订阅。')}
          >
            立即升级
          </Button>
        </div>
      )}

      <div className="tm-stack">
        <section className="tm-card" aria-labelledby="tm-plan-heading">
          <div className="tm-card-head">
            <h2 id="tm-plan-heading" className="tm-card-title">
              <CreditCardOutlined /> 套餐与订阅
            </h2>
          </div>
          <div className="tm-card-body">
            <div className="tm-plan-grid">
              <div className="tm-plan-cell">
                <span className="tm-k">计费档位</span>
                <div className="tm-v">
                  <span>{billingTitle}</span>
                  {isFreeTier ? <span className="tm-chip tm-chip--free">免费</span> : null}
                  {!isFreeTier ? <span className="tm-chip tm-chip--paid">付费</span> : null}
                </div>
              </div>
              <div className="tm-plan-cell">
                <span className="tm-k">成员数量</span>
                <div className="tm-v">
                  <span>{memberDisplay}</span>
                  {atFreeCeiling ? <span className="tm-chip tm-chip--danger">已达上限</span> : null}
                </div>
              </div>
              <div className="tm-plan-cell">
                <span className="tm-k">订阅到期</span>
                <div className="tm-v">
                  {team.planTier === 'paid' && team.subscriptionEndsAt ? (
                    <>
                      <span>{dayjs(team.subscriptionEndsAt).format('YYYY-MM-DD')}</span>
                      {paidOk ? (
                        <span className="tm-chip tm-chip--active">有效</span>
                      ) : (
                        <span className="tm-chip tm-chip--danger">已到期</span>
                      )}
                    </>
                  ) : (
                    <span>未开通付费订阅（按免费版规则）</span>
                  )}
                </div>
              </div>
              <div className="tm-plan-cell">
                <span className="tm-k">团队编码</span>
                <div className="tm-v">
                  <span className="tm-code">{team.code}</span>
                  <Button
                    type="text"
                    className="tm-copy-btn"
                    icon={<CopyOutlined />}
                    aria-label="复制团队编码"
                    onClick={() => copyTeamCode(team.code)}
                  />
                </div>
              </div>
              {planNote ? (
                <div className="tm-plan-cell tm-plan-cell--full">
                  <p className="tm-plan-note">{planNote}</p>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="tm-card" aria-labelledby="tm-basic-heading">
          <div className="tm-card-head tm-card-head--row">
            <h2 id="tm-basic-heading" className="tm-card-title">
              <InfoCircleOutlined /> 基本信息
            </h2>
            <Button className="tm-btn-ghost" icon={<UserOutlined />} onClick={() => navigate('/members')}>
              成员管理
            </Button>
          </div>
          <div className="tm-card-body">
            {canEdit && !suspended ? (
              <Form form={form} layout="vertical" onFinish={handleSave}>
                <Row gutter={[24, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="name" label="团队名称" rules={[{ required: true, message: '请填写' }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="category" label="团队类型">
                      <Select allowClear placeholder="请选择" options={CATEGORY_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="orgName" label="单位名称">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="teamSize" label="团队规模">
                      <Select allowClear placeholder="请选择" options={TEAM_SIZE_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="ownerPosition" label="您的职务">
                      <Select allowClear placeholder="请选择" options={POSITION_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="industry" label="所在行业">
                      <Select allowClear placeholder="请选择" options={INDUSTRY_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="researchField" label="研究领域">
                      <Select allowClear placeholder="请选择" options={RESEARCH_OPTIONS} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="intro" label="简介">
                      <TextArea rows={4} placeholder="简要介绍团队方向与特色" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="tm-contact-divider" />
                <h3 className="tm-contact-heading">联系信息</h3>
                <Row gutter={[24, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="contactName" label="联系人">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="contactPhone" label="联系电话">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item name="contactEmail" label="联系邮箱">
                      <Input type="email" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="tm-form-actions">
                  <Button className="tm-btn-discard" type="text" onClick={handleDiscard}>
                    放弃更改
                  </Button>
                  <Button className="tm-btn-save" type="primary" htmlType="submit" loading={saving}>
                    保存资料设置
                  </Button>
                </div>
              </Form>
            ) : (
              <div className="tm-ro-grid">
                {readOnlyRows.map((row) => (
                  <div key={row.k} className="tm-ro-item">
                    <span className="tm-k">{row.k}</span>
                    <span className="tm-ro-val">{row.v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TeamManagement;
